/**
 * Client replica (SPEC §6.1): the household Y.Doc lives in IndexedDB (y-indexeddb) and
 * syncs over a same-origin WebSocket (y-partyserver provider). The UI renders from local
 * state; the network is never on the critical path.
 */
import * as Y from 'yjs'
import { IndexeddbPersistence } from 'y-indexeddb'
import YProvider from 'y-partyserver/provider'
import { Awareness } from 'y-protocols/awareness'

/** Origin of every local UI transaction (tracked by local undo, SPEC §6.2). */
export class LocalOrigin {
  constructor(public label = 'local') {}
}

export interface SyncStatus {
  /** Local replica loaded from IndexedDB. */
  local: boolean
  /** WebSocket connected. */
  connected: boolean
  /** Initial sync with the server completed at least once this session. */
  synced: boolean
  online: boolean
  lastSyncedAt: number | null
  /** Server refused the socket (session expired). */
  authFailed: boolean
}

const DB_NAME = 'terrazzo-household'

class Sync {
  readonly doc = new Y.Doc()
  readonly awareness = new Awareness(this.doc)
  private idb: IndexeddbPersistence | null = null
  private provider: YProvider | null = null
  private listeners = new Set<() => void>()
  private failures = 0
  status: SyncStatus = {
    local: false,
    connected: false,
    synced: false,
    online: typeof navigator === 'undefined' ? true : navigator.onLine,
    lastSyncedAt: Number(localStorage.getItem('tz.lastSyncedAt') ?? '') || null,
    authFailed: false,
  }

  private set(patch: Partial<SyncStatus>) {
    this.status = { ...this.status, ...patch }
    for (const l of this.listeners) l()
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }
  getStatus = () => this.status

  /** Load the local replica. Resolves once IndexedDB content is applied. */
  async openLocal(): Promise<void> {
    if (this.idb) return this.idb.whenSynced.then(() => undefined)
    this.idb = new IndexeddbPersistence(DB_NAME, this.doc)
    await this.idb.whenSynced
    this.set({ local: true })
  }

  connect(user: { id: string; name: string }): void {
    if (this.provider) return
    this.awareness.setLocalStateField('user', { id: user.id, name: user.name })
    const secure = location.protocol === 'https:'
    this.provider = new YProvider(location.host, 'main', this.doc, {
      prefix: '/party/household/main',
      awareness: this.awareness,
      protocol: secure ? 'wss' : 'ws',
      maxBackoffTime: 10_000,
      connect: true,
    })
    this.provider.on('status', ({ status }: { status: string }) => {
      const connected = status === 'connected'
      if (connected) this.failures = 0
      this.set({ connected })
    })
    this.provider.on('sync', (isSynced: boolean) => {
      if (isSynced) {
        const now = Date.now()
        localStorage.setItem('tz.lastSyncedAt', String(now))
        this.set({ synced: true, lastSyncedAt: now })
      }
    })
    this.provider.on('connection-close', () => {
      this.failures++
      // Repeated immediate closes usually mean the session cookie is gone.
      if (this.failures >= 3 && navigator.onLine) this.checkAuth()
    })
    window.addEventListener('online', () => {
      this.set({ online: true })
      this.provider?.connect()
    })
    window.addEventListener('offline', () => this.set({ online: false }))
  }

  private async checkAuth() {
    try {
      const r = await fetch('/api/me', { credentials: 'same-origin' })
      if (r.status === 401) {
        this.provider?.disconnect()
        this.set({ authFailed: true })
      }
    } catch {
      /* offline */
    }
  }

  reconnect() {
    this.provider?.disconnect()
    void this.provider?.connect()
  }

  setEditing(itemId: string | null) {
    const cur = this.awareness.getLocalState()?.editing ?? null
    if (cur !== itemId) this.awareness.setLocalStateField('editing', itemId)
  }

  /** Sign out: drop the socket and wipe the local replica. */
  async wipe(): Promise<void> {
    this.provider?.destroy()
    this.provider = null
    await this.idb?.clearData()
    this.idb = null
  }
}

export const sync = new Sync()
export const doc = sync.doc

/** Ask the browser to keep our IndexedDB (SPEC §6.1); remember the answer for Diagnostics. */
export async function requestPersistence(): Promise<boolean | null> {
  try {
    if (!navigator.storage?.persist) return null
    if (await navigator.storage.persisted()) {
      localStorage.setItem('tz.persisted', '1')
      return true
    }
    if (localStorage.getItem('tz.persistAsked')) return localStorage.getItem('tz.persisted') === '1'
    localStorage.setItem('tz.persistAsked', '1')
    const ok = await navigator.storage.persist()
    localStorage.setItem('tz.persisted', ok ? '1' : '0')
    return ok
  } catch {
    return null
  }
}
